import { DateTime } from 'luxon'
import { roundMinutesUpTo15 } from 'util/roundMinutesUpTo15'

export type Period = 'AM' | 'PM'

export function to24Hour(hour12: number, period: Period): number {
  return (hour12 % 12) + (period === 'PM' ? 12 : 0)
}

export function to12Hour(hour24: number): number {
  return hour24 % 12 === 0 ? 12 : hour24 % 12
}

/**
 * Whether the locale writes times with AM/PM. `locale` is only for tests.
 * Prefer `nativeTimeInputUsesTwelveHourClock`: Chrome honours the OS
 * "24-hour time" setting in <input type="time"> but not in Intl.
 */
export function usesTwelveHourClock(locale?: string): boolean {
  return new Intl.DateTimeFormat(locale, { hour: 'numeric' })
    .formatToParts(new Date(2000, 0, 1, 13))
    .some((part) => part.type === 'dayPeriod')
}

let nativeTwelveHourClock: boolean | undefined

/**
 * Whether the native <input type="time"> shows an AM/PM segment, read from
 * the control itself so the picker's columns always match it. Probed once
 * per page load (a changed OS clock setting takes effect on reload) so
 * mounting many pickers costs one layout, not one each.
 */
export function nativeTimeInputUsesTwelveHourClock(): boolean {
  if (typeof document === 'undefined') return usesTwelveHourClock()
  nativeTwelveHourClock ??= detectNativeTwelveHourClock()
  return nativeTwelveHourClock
}

/**
 * A hidden probe input gets its AM/PM segment styled tall; the input only
 * grows when that segment exists. The hour segment, which always exists,
 * proves the probe works in this browser; where it doesn't, the locale
 * decides. Chromium names the segment `ampm`, WebKit `meridiem`.
 */
function detectNativeTwelveHourClock(): boolean {
  const segmentExists = (segment: 'hour' | 'ampm' | 'meridiem') => {
    const style = document.createElement('style')
    style.textContent = `input[data-clock-probe]::-webkit-datetime-edit-${segment}-field{display:block;height:400px}`
    const probe = document.createElement('input')
    probe.type = 'time'
    probe.value = '13:05'
    probe.setAttribute('data-clock-probe', '')
    probe.style.cssText = 'position:absolute;left:-9999px;top:0'
    document.body.append(style, probe)
    const grown = probe.getBoundingClientRect().height > 300
    probe.remove()
    style.remove()
    return grown
  }
  if (!segmentExists('hour')) return usesTwelveHourClock()
  return segmentExists('ampm') || segmentExists('meridiem')
}

/**
 * The picker's column choices. `hour` is 1-12 with a 12-hour clock and 0-23
 * with a 24-hour clock, where `period` stays null.
 */
export type TimeDraft = {
  hour: number | null
  minute: number | null
  period: Period | null
}

export const EMPTY_TIME_DRAFT: TimeDraft = {
  hour: null,
  minute: null,
  period: null,
}

/** The fully chosen draft for an existing value. */
export function timeDraftFromValue(
  time: { hour: number; minute: number },
  twelveHour: boolean
): TimeDraft {
  return twelveHour
    ? {
        hour: to12Hour(time.hour),
        minute: time.minute,
        period: time.hour >= 12 ? 'PM' : 'AM',
      }
    : { hour: time.hour, minute: time.minute, period: null }
}

/**
 * The time a draft describes, or null while it is still incomplete: no hour
 * yet, or no AM/PM on a 12-hour clock. The minute defaults to :00.
 */
export function resolveTimeDraft(
  draft: TimeDraft,
  twelveHour: boolean
): { hour: number; minute: number } | null {
  if (draft.hour === null) return null
  if (twelveHour && draft.period === null) return null
  return {
    hour: twelveHour
      ? to24Hour(draft.hour, draft.period as Period)
      : draft.hour,
    minute: draft.minute ?? 0,
  }
}

/**
 * The time "Select Now" picks, and the time a day gets when none is set yet:
 * the current minute, or the next quarter hour when a caller opts in.
 */
export function nextSelectableTime(
  now: DateTime,
  roundUpToQuarterHour: boolean
): DateTime {
  const minute = now.startOf('minute')
  return roundUpToQuarterHour ? roundMinutesUpTo15(minute) : minute
}

/**
 * Bounds the months the calendar can navigate to.
 *
 * With captionLayout="dropdown", react-day-picker defaults navigation to
 * today-100y .. end of the current year when no range is given, so the year
 * dropdown lists no future years and the Next arrow dies at December. Use the
 * allowed window instead, or ±10 years when unbounded. The current value
 * always stays reachable, and the end never precedes the start (callers can
 * pass max < min, e.g. assignedAt+365d vs now), because an inverted range
 * renders no month at all.
 */
export function getCalendarNavRange({
  min,
  max,
  value,
  now,
}: {
  min?: DateTime
  max?: DateTime
  value?: DateTime | null
  now: DateTime
}): { startMonth: Date; endMonth: Date } {
  const present = (d: DateTime | null | undefined): d is DateTime => !!d
  const navStart = DateTime.min(
    ...[min ?? now.minus({ years: 10 }), value].filter(present)
  )
  const navEnd = DateTime.max(
    ...[max ?? now.plus({ years: 10 }), value, navStart].filter(present)
  )
  return {
    startMonth: navStart.startOf('month').toJSDate(),
    endMonth: navEnd.endOf('month').toJSDate(),
  }
}
